import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type AuthUser = {
  id: string; email: string; firstName: string; lastName: string; role: string;
  active?: boolean; permissions?: string[]; invigilatorCourses?: string[] | string;
  [key: string]: any;
};

type AuthState = {
  user: AuthUser | null;
  isLoading: boolean;
  loginWithRedirect: (options?: { redirectUrl?: string }) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError('');
    const response = await fetch('/api/auth/login', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, password }) });
    if (!response.ok) { const value = await response.json().catch(() => ({})); setError(value.message || 'Sign-in failed'); setBusy(false); return; }
    window.location.assign(new URLSearchParams(location.search).get('return') || '/');
  };
  return <main style={{minHeight:'100vh',display:'grid',placeItems:'center',background:'#f3eee5',padding:24}}><form onSubmit={submit} style={{width:'100%',maxWidth:420,background:'#fff',border:'1px solid #d8d1c6',borderRadius:18,padding:32,boxShadow:'0 20px 60px rgba(23,37,31,.12)'}}><div style={{fontSize:12,fontWeight:800,letterSpacing:'.14em',textTransform:'uppercase',color:'#9a5b39'}}>Rattlebone</div><h1 style={{fontSize:30,margin:'8px 0 4px',color:'#17251f'}}>Staff Hub</h1><p style={{color:'#66716b',margin:'0 0 24px'}}>Private staff access</p><label style={{display:'block',fontWeight:700,fontSize:13,marginBottom:6}}>Email</label><input autoComplete="username" type="email" required value={email} onChange={e=>setEmail(e.target.value)} style={{width:'100%',padding:12,border:'1px solid #c9c3b9',borderRadius:10,marginBottom:16}}/><label style={{display:'block',fontWeight:700,fontSize:13,marginBottom:6}}>Password</label><input autoComplete="current-password" type="password" required value={password} onChange={e=>setPassword(e.target.value)} style={{width:'100%',padding:12,border:'1px solid #c9c3b9',borderRadius:10,marginBottom:12}}/>{error&&<p role="alert" style={{color:'#a62b2b',fontSize:13}}>{error}</p>}<button disabled={busy} style={{width:'100%',padding:13,border:0,borderRadius:10,background:'#17251f',color:'#fff',fontWeight:800,cursor:'pointer'}}>{busy?'Signing in…':'Sign in'}</button><p style={{fontSize:12,color:'#66716b',marginTop:18}}>Sessions expire after 30 minutes of inactivity. Sign out on shared devices.</p></form></main>;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    const response = await fetch('/api/auth/session', { credentials: 'same-origin', cache: 'no-store' });
    setUser(response.ok ? (await response.json()).user : null); setLoading(false);
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  const loginWithRedirect = useCallback((options?: {redirectUrl?: string}) => {
    if (location.pathname === '/sign-in') return;
    const target = options?.redirectUrl || location.href;
    location.assign(`/sign-in?return=${encodeURIComponent(target)}`);
  }, []);
  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    sessionStorage.clear(); localStorage.clear();
    if ('caches' in window) for (const key of await caches.keys()) await caches.delete(key);
    location.assign('/sign-in');
  }, []);
  if (location.pathname === '/sign-in') return <SignIn />;
  return <AuthContext.Provider value={{ user, isLoading, loginWithRedirect, logout, refresh }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
